<template>
  <div class="verification-manual-shell">
    <div v-if="showHistoryTabs && available_tabs.length" class="verification-manual-tabs mb-3" role="tablist" aria-label="Manual verification history tabs">
      <button
        v-for="tab in available_tabs"
        :key="tab.id"
        type="button"
        :class="['verification-manual-tabs__tab', { 'is-active': active_tab === tab.id }]"
        @click="active_tab = tab.id">
        {{ tab.label }}
      </button>
    </div>

    <div class="verification-manual-tab-content">
      <template v-if="active_tab === 'manual-review-checklist'">
        <div class="verification-manual-info-card">
          <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
            <div>
              <h2 class="verification-manual-section-title mb-1">Assigned Manual Verification Questions</h2>
              <p class="text-muted small mb-0 d-none">Only questions mapped to the configured manual verification form are shown here.</p>
            </div>
            <span class="badge rounded-pill text-bg-light border">Completed</span>
          </div>

          <div class="verification-manual-decision-panel verification-manual-decision-panel--compact mb-3">
            <div class="verification-manual-decision-header">
              <div v-if="decisionActor || decisionTimestamp" class="verification-manual-section-title mb-0">
                Reviewed by: {{ decisionActor || 'Reviewer' }}<span v-if="decisionTimestamp"> ({{ format_date_only(decisionTimestamp) }})</span>
              </div>
              <div v-if="decisionLabel" :class="['verification-manual-decision-summary', `is-${decisionTone}`]">
                <i class="bi bi-check2-circle" aria-hidden="true"></i>
                <span>Decision taken: {{ decisionLabel }}</span>
              </div>
            </div>
            <div class="verification-manual-decision-grid">
              <div>
                <label class="verification-manual-decision-label">Comments</label>
                <textarea :value="decisionComment" class="verification-manual-decision-textarea" rows="2" readonly></textarea>
              </div>
              <div>
                <label class="verification-manual-decision-label">Decision</label>
                <div class="verification-manual-decision-actions">
                  <button type="button" :class="['verification-manual-decision-button is-approve', { 'is-selected': decisionAction === 'approve' }]" disabled>
                    <i class="bi bi-hand-thumbs-up"></i>
                    <strong>Approve</strong>
                    <span>Move to Approved</span>
                  </button>
                  <button type="button" :class="['verification-manual-decision-button is-changes', { 'is-selected': decisionAction === 'request_changes' }]" disabled>
                    <i class="bi bi-shield-exclamation"></i>
                    <strong>Request Changes</strong>
                    <span>Return to Applicant</span>
                  </button>
                  <button type="button" :class="['verification-manual-decision-button is-reject', { 'is-selected': decisionAction === 'reject' }]" disabled>
                    <i class="bi bi-x-circle"></i>
                    <strong>Reject</strong>
                    <span>Move to Rejected</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="decisionLabel" class="alert alert-warning mb-3 py-2">
            This manual review is already {{ normalized_status_label.toLowerCase() }}. Field outcomes are read-only.
          </div>

          <div v-if="!normalized_review_sections.length && !normalized_field_runs.length" class="text-muted">
            No manual verification questions were assigned to this manual verification form.
          </div>

          <div
            v-else-if="!normalized_review_sections.length && normalized_field_runs.length"
            class="verification-manual-review-form"
          >
            <div class="verification-manual-review-rule-header">
              <div class="verification-manual-review-rule-header__icon">
                <i class="bi bi-ui-checks"></i>
              </div>
              <div class="min-w-0">
                <div class="verification-manual-review-rule-header__eyebrow">Manual verification rule</div>
                <div class="verification-manual-review-rule-header__title">Reviewed fields</div>
              </div>
              <span class="verification-manual-review-rule-header__badge">Mapped review</span>
            </div>

            <div class="verification-manual-checklist">
              <article
                v-for="field_run in normalized_field_runs"
                :key="field_run.id"
                class="verification-manual-checklist-row"
              >
                <div class="verification-manual-checklist-row__main">
                  <div class="verification-manual-checklist-row__label">{{ field_run.field_label_snapshot || 'Reviewed field' }}</div>
                  <div class="verification-manual-checklist-row__detail">
                    {{ field_run.source_evidence ? `Review against submitted value for ${field_run.source_evidence}.` : 'Review against the submitted applicant value.' }}
                  </div>
                </div>
                <div class="verification-manual-checklist-row__answer-panel">
                  <span>Submitted value</span>
                  <div class="verification-manual-checklist-row__answer-line">
                    <strong class="verification-manual-checklist-row__answer">{{ format_detail_value(field_run.user_answer) }}</strong>
                  </div>
                </div>
                <div class="verification-manual-checklist-row__options">
                  <button
                    type="button"
                    :class="['verification-manual-review-choice', 'is-success', { 'is-selected': normalize_field_run_status(field_run) === 'pass' }]"
                    disabled
                  >
                    <i class="bi bi-check-circle"></i>
                    <span>Pass</span>
                  </button>
                  <button
                    type="button"
                    :class="['verification-manual-review-choice', 'is-danger', { 'is-selected': normalize_field_run_status(field_run) === 'fail' }]"
                    disabled
                  >
                    <i class="bi bi-x-circle"></i>
                    <span>Fail</span>
                  </button>
                  <button
                    type="button"
                    :class="['verification-manual-review-choice', 'is-muted', { 'is-selected': normalize_field_run_status(field_run) === 'na' }]"
                    disabled
                  >
                    <i class="bi bi-dash-circle"></i>
                    <span>N/A</span>
                  </button>
                </div>
                <div v-if="field_run.reviewer_comment" class="verification-manual-checklist-row__comment">{{ field_run.reviewer_comment }}</div>
              </article>
            </div>
          </div>

          <div
            v-for="form_section in normalized_review_sections"
            :key="form_section.id"
            class="verification-manual-review-form"
          >
            <div class="verification-manual-review-rule-header">
              <div class="verification-manual-review-rule-header__icon">
                <i class="bi bi-ui-checks"></i>
              </div>
              <div class="min-w-0">
                <div class="verification-manual-review-rule-header__eyebrow">Manual verification rule</div>
                <div class="verification-manual-review-rule-header__title">{{ form_section.form_name }}</div>
              </div>
              <span class="verification-manual-review-rule-header__badge">Mapped review</span>
            </div>

            <template v-for="section in form_section.sections" :key="section.id">
              <div class="verification-manual-subsection-label">{{ section.label }}</div>
              <template v-for="sub_section in section.sub_sections" :key="sub_section.id">
                <div
                  v-if="sub_section.label && sub_section.label !== section.label"
                  class="verification-manual-subsection-eyebrow"
                >
                  {{ sub_section.label }}
                </div>
                <div class="verification-manual-checklist">
                  <article
                    v-for="item in sub_section.questions"
                    :key="item.id"
                    class="verification-manual-checklist-row"
                  >
                    <div class="verification-manual-checklist-row__main">
                      <div class="verification-manual-checklist-row__label">{{ item.label }}</div>
                      <div class="verification-manual-checklist-row__detail">{{ item.detail }}</div>
                    </div>
                    <div class="verification-manual-checklist-row__answer-panel">
                      <span>Submitted value</span>
                      <div class="verification-manual-checklist-row__answer-line">
                        <strong class="verification-manual-checklist-row__answer">{{ format_detail_value(item.answer) }}</strong>
                        <button
                          v-if="question_has_preview(item)"
                          type="button"
                          class="verification-manual-preview-button"
                          @click="$emit('preview-question', item)"
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                    <div class="verification-manual-checklist-row__options">
                      <button
                        type="button"
                        :class="['verification-manual-review-choice', 'is-success', { 'is-selected': item.status === 'pass' }]"
                        disabled
                      >
                        <i class="bi bi-check-circle"></i>
                        <span>Pass</span>
                      </button>
                      <button
                        type="button"
                        :class="['verification-manual-review-choice', 'is-danger', { 'is-selected': item.status === 'fail' }]"
                        disabled
                      >
                        <i class="bi bi-x-circle"></i>
                        <span>Fail</span>
                      </button>
                      <button
                        type="button"
                        :class="['verification-manual-review-choice', 'is-muted', { 'is-selected': item.status === 'na' }]"
                        disabled
                      >
                        <i class="bi bi-dash-circle"></i>
                        <span>N/A</span>
                      </button>
                    </div>
                    <div v-if="item.comment" class="verification-manual-checklist-row__comment">{{ item.comment }}</div>
                  </article>
                </div>
              </template>
            </template>
          </div>
        </div>
      </template>

      <template v-else-if="active_tab === 'manual-applicant-information'">
        <ApplicationDetailsReview layout="accordion" :form_sections="applicant_form_sections" />
      </template>

      <template v-else-if="active_tab === 'manual-documents'">
        <div id="manual-documents">
          <h2 class="verification-manual-section-title">Submitted Documents</h2>
          <div v-if="!normalized_documents.length" class="verification-manual-info-card text-muted">
            No applicant-submitted documents were returned by the backend.
          </div>
          <div v-else class="verification-manual-document-grid">
            <div
              v-for="document in normalized_documents"
              :key="document.id"
              class="verification-manual-document-card"
            >
              <div class="verification-manual-document-card__icon">
                <i class="bi bi-file-earmark-pdf"></i>
              </div>
              <div class="min-w-0 flex-grow-1">
                <div class="d-flex align-items-center justify-content-between gap-2">
                  <div class="verification-manual-document-card__title text-truncate">{{ document.name }}</div>
                  <span :class="['badge rounded-pill fw-normal', document.status_class]">{{ document.status_label }}</span>
                </div>
                <div class="verification-manual-document-card__file text-truncate">{{ document.file_name || 'No file name' }}</div>
                <div class="d-flex justify-content-end mt-2">
                  <button
                    type="button"
                    class="verification-manual-view-button"
                    :disabled="!document.url"
                    @click="$emit('preview-document', document)"
                  >
                    View <i class="bi bi-eye"></i>
                  </button>
                </div>
              </div>
              <button type="button" class="verification-manual-document-menu" aria-label="Document options" disabled>
                <i class="bi bi-three-dots-vertical"></i>
              </button>
            </div>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script src="./manual_verification_history_view.js"></script>

<style scoped src="../../../views/onboarding_system_views/Gates/Verification/manage_verification.css"></style>
